import classNames from 'classnames';
import Badge, {IBadgeProps} from 'components/ui/Badge/Badge';
import {differenceInDays} from 'date-fns';
import {translate} from 'helpers/localize';
import React, {useMemo} from 'react';

import styles from './StatusCell.module.scss';

export const getStatusVariant = (
    status: string | null,
): IBadgeProps['color'] => {
    switch (status) {
        case 'needs_signature':
        case 'compliance':
        case 'overdue':
        case 'error':
        case 'deleted':
            return 'danger';
        case 'pending':
        case 'inactive':
            return 'warn';
        case 'transferred':
        case 'executed':
        case 'paid':
        case 'active':
            return 'success';
        case 'reverted':
        case 'verification':
            return 'successOutline';
        default:
            return 'primary';
    }
};

export const getTransactionStatusText = (status: string | null): string => {
    switch (status) {
        case 'needs_signature':
            return 'not signed';
        case 'transferred':
            return 'executed';
        case 'paid':
            return 'paid';
        case 'draft':
            return 'draft';
        case 'active':
            return 'active';
        case 'inactive':
            return 'inactive';
        case 'executed':
            return 'executed';
        default:
            return status ?? '';
    }
};

export const getPaymentRequestStatusText = (
    pending: boolean,
    dueDateStr: string,
    draft: boolean,
) => {
    if (draft) {
        return 'Draft';
    }

    if (!pending) {
        return 'Paid';
    }

    const now = new Date();
    const dueDate = new Date(dueDateStr);

    if (now > dueDate) {
        const overDueDays = differenceInDays(now, dueDate);
        return overDueDays > 0 ? (
            <span data-notranslate>
                {translate('%{days-count} days late', {
                    'days-count': overDueDays,
                })}
            </span>
        ) : (
            <span data-notranslate>{translate('Due today')}</span>
        );
    }

    return 'Pending';
};

export interface ITransactionStatusCellProps {
    status: string | null;
    type: 'transaction';
    compliance?: boolean;
}

export interface IPaymentRequestStatusCellProps {
    type: 'paymentRequest';
    pending: boolean;
    dueDate: string;
    draft: boolean;
}

export interface IAutomationStatusCellProps {
    type: 'automation';
    status: string;
    active: boolean;
}

export type StatusCellProps =
    | ITransactionStatusCellProps
    | IPaymentRequestStatusCellProps
    | IAutomationStatusCellProps;

const StatusCell = (props: StatusCellProps) => {
    const status = useMemo(() => {
        if (isPaymentRequestStatusCell(props)) {
            const {pending, dueDate, draft} = props;
            if (draft) return 'draft';
            if (!pending) return 'paid';
            if (pending && new Date(dueDate) < new Date()) return 'overdue';
            return 'pending';
        }

        if (isAutomationStatusCell(props)) {
            if (props.status === 'triggerable') {
                return props.active ? 'active' : 'inactive';
            }
            return props.status;
        }
        return props.status;
    }, [props]);

    const getContent = () => {
        if (isPaymentRequestStatusCell(props)) {
            const {pending, dueDate, draft} = props;
            return getPaymentRequestStatusText(pending, dueDate, draft);
        }

        return getTransactionStatusText(status);
    };

    const getComplianceContent = () => {
        if (
            isPaymentRequestStatusCell(props) ||
            isAutomationStatusCell(props)
        ) {
            return null;
        }

        const {compliance} = props;
        if (compliance) {
            return (
                <Badge
                    className={classNames(
                        styles.status,
                        styles.complianceStatus,
                    )}
                    color={getStatusVariant('compliance')}
                    content="needs validation"
                />
            );
        }

        return null;
    };

    return (
        <div className={styles.badgeContainer}>
            <Badge
                className={styles.status}
                color={getStatusVariant(status)}
                content={getContent()}
            />

            {getComplianceContent()}
        </div>
    );
};

export default StatusCell;

const isPaymentRequestStatusCell = (
    props: StatusCellProps,
): props is IPaymentRequestStatusCellProps => {
    return props.type === 'paymentRequest';
};

const isAutomationStatusCell = (
    props: StatusCellProps,
): props is IAutomationStatusCellProps => {
    return props.type === 'automation';
};
